import React from "react";
import moment from "moment";
import { GiCancel } from "react-icons/gi";
import { ActivityLogProps } from "../../types/activityLog";

import styles from "./ActivityLogDetailModal.module.scss";

type Props = {
  log: ActivityLogProps;
  modelNameMap: Record<string, string>;
  onClose: () => void;
};

const isImageField = (
  key: string,
  references: Record<string, string> | null
): boolean => {
  return key === "image" && !!references?.image;
};

const renderValue = (
  key: string,
  value: any,
  references: Record<string, string> | null,
  colorClass: string
) => {
  if (isImageField(key, references) && value) {
    const imageUrl = references!.image;
    return (
      <a href={imageUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={imageUrl}
          alt="image"
          className="w-[100px] h-[100px] object-cover rounded border cursor-pointer hover:opacity-80"
        />
      </a>
    );
  }

  const display =
    value !== null && value !== undefined ? String(value) : "-";
  const text =
    references && references[key] && value
      ? `${display} (${references[key]})`
      : display;

  return <span className={`${colorClass} break-all text-sm`}>{text}</span>;
};

const ActivityLogDetailModal = ({ log, modelNameMap, onClose }: Props) => {
  return (
    <div className={styles.ActivityLogDetailModal}>
      <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-[80vw] lg:h-fit shadow-lg lg:rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-2xl mt-10 lg:mt-0 text-center lg:text-left font-bold leading-6 text-primary-1">
            એક્ટિવિટી લોગ વિગતો -{" "}
            {modelNameMap[log.model_name] || log.model_name} (ID:{" "}
            {log.model_id})
          </h3>

          <GiCancel
            onClick={onClose}
            className="absolute cursor-pointer z-10 shadow bg-white top-3 right-5 text-3xl lg:text-xl text-primary-1 border-2 border-primary-1 p-1 lg:p-0 rounded-full"
          />

          <div className="mt-2 py-3 lg:py-2">
            <div className="p-4 pb-0">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 border-2 border-primary-1 rounded-md p-4">
                <div>
                  <span className="font-semibold text-primary-2 text-sm">
                    યુઝર
                  </span>
                  <p className="text-primary-1 font-bold">
                    {log.user?.name || "-"}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-primary-2 text-sm">
                    તારીખ
                  </span>
                  <p className="text-primary-1 font-bold">
                    {moment(log.created_at).format("DD-MM-YYYY hh:mm A")}
                  </p>
                </div>
                <div>
                  <span className="font-semibold text-primary-2 text-sm">
                    એકશન
                  </span>
                  <p className="mt-1">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-bold text-white ${
                        log.action === "update" ? "bg-blue-500" : "bg-red-500"
                      }`}
                    >
                      {log.action === "update" ? "અપડેટ" : "ડિલીટ"}
                    </span>
                  </p>
                </div>
                {log.ip_address && (
                  <div>
                    <span className="font-semibold text-primary-2 text-sm">
                      IP એડ્રેસ
                    </span>
                    <p className="text-primary-1 font-bold">
                      {log.ip_address}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {log.old_values && (
                  <div className="border-2 border-red-300 rounded-md p-4">
                    <h2 className="text-xl lg:text-lg font-bold text-red-600 mb-3">
                      જૂની વિગતો
                    </h2>
                    {Object.entries(log.old_values).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex py-2 border-b border-red-100 last:border-0"
                      >
                        <span className="font-semibold text-gray-600 w-40 shrink-0 text-sm">
                          {key}:
                        </span>
                        {renderValue(key, value, log.references, "text-red-500")}
                      </div>
                    ))}
                  </div>
                )}

                {log.new_values && (
                  <div className="border-2 border-green-300 rounded-md p-4">
                    <h2 className="text-xl lg:text-lg font-bold text-green-600 mb-3">
                      નવી વિગતો
                    </h2>
                    {Object.entries(log.new_values).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex py-2 border-b border-green-100 last:border-0"
                      >
                        <span className="font-semibold text-gray-600 w-40 shrink-0 text-sm">
                          {key}:
                        </span>
                        {renderValue(key, value, log.references, "text-green-600")}
                      </div>
                    ))}
                  </div>
                )}

                {log.action === "delete" && !log.new_values && (
                  <div className="border-2 border-gray-300 rounded-md p-4">
                    <h2 className="text-xl lg:text-lg font-bold text-gray-400 mb-3">
                      નવી વિગતો
                    </h2>
                    <p className="text-gray-400 text-sm">
                      રેકોર્ડ ડિલીટ થયેલ છે
                    </p>
                  </div>
                )}
              </div>

              <div className="grid lg:flex gap-3 mt-3 justify-center">
                <div className="text-center">
                  <button
                    className="mx-auto shadow rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                    type="button"
                    onClick={onClose}
                  >
                    બંધ કરો
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogDetailModal;
